import type { TemplateSettings as GrapesSwipeTemplateSettings } from 'opensolar-common'
import { ProposalTemplateTypeEnum, ProposalV2TemplateType } from 'resources/proposalTemplates/types'
import type { BespokeConfig, GrapesSwipeConfig, NonSolarConfig, ProposalV2Config } from 'types/proposalTemplate'

export const getProposalV2GrapesSwipeConfig = (templateSettings: GrapesSwipeTemplateSettings): GrapesSwipeConfig => {
  return {
    view_config: {
      proposal_name: 'grapes_swipe',
      pathname: 'v1',
    },
    type: ProposalTemplateTypeEnum.proposalV2_grapes_swipe,
    template_settings: templateSettings,
  }
}

/**
 * Default `proposal_v2_config` payload for a freshly created Non-Solar
 * proposal template (GS-7418).
 *
 * `view_config.version` is bumped to `'1.1'` because the structured
 * `template_settings` shape changed from `{ title: string }` to the typed
 * `NonSolarTemplateSettings`. The iframe (`spa/proposals/non-solar/`) treats
 * a missing field as "feature disabled", so legacy `1.0` payloads continue to
 * render correctly without a backfill migration.
 */
export const getDefaultProposalV2NonSolarConfig = (): NonSolarConfig => {
  return {
    view_config: {
      proposal_name: 'non_solar',
      pathname: 'non-solar',
      version: '1.1',
    },
    type: ProposalTemplateTypeEnum.proposalV2_nonsolar,
    template_settings: {
      contract: { enabled: true, scope: 'perProject', templateId: null },
      coverImage: { enabled: true, url: null },
      language: 'default',
      caseStudies: [],
      intro: { enabled: false, bodyHtml: '' },
      headingCaseStudies: '',
    },
  }
}

export const getDefaultProposalV2BespokeConfig = (): BespokeConfig => {
  return {
    type: ProposalTemplateTypeEnum.proposalV2_bespoke,
    view_config: {
      proposal_name: 'bespoke',
      pathname: 'bespoke',
      version: '1.0',
    },
    template_settings: {},
  }
}

/**
 * Dispatcher for non-Grapes-Swipe Proposal V2 default configs (non-solar and
 * bespoke V2). Grapes Swipe has its own builder (`getProposalV2GrapesSwipeConfig`)
 * because its template settings are loaded lazily and asynchronously from
 * `opensolar-common`; do not route Grapes Swipe through this dispatcher.
 */
export const getDefaultProposalV2Config = (type: ProposalV2TemplateType): ProposalV2Config => {
  switch (type) {
    case ProposalTemplateTypeEnum.proposalV2_nonsolar:
      return getDefaultProposalV2NonSolarConfig()
    case ProposalTemplateTypeEnum.proposalV2_bespoke:
      return getDefaultProposalV2BespokeConfig()
    case ProposalTemplateTypeEnum.proposalV2_grapes_swipe:
      throw new Error(
        'getDefaultProposalV2Config does not support Grapes Swipe. Use getProposalV2GrapesSwipeConfig directly.'
      )
  }
}

export function mergeTemplateSettingsIntoProposalV2Config(
  existing: ProposalV2Config,
  templateSettings: any
): ProposalV2Config {
  return {
    ...existing,
    template_settings: templateSettings,
  }
}
